// Smart Daara - Console IA admin école (fonctions F1/F4/F7, limites)
import React, { useEffect, useState } from 'react';
import { useLanguage } from '../../contexts/LanguageContext';
import { aiAPI } from '../../lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Switch } from '../../components/ui/switch';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Button } from '../../components/ui/button';
import { Sparkles } from 'lucide-react';
import { toast } from 'sonner';

const FN_LABELS = {
  f1: ['Génération de devoirs', 'Assignment generation'],
  f4: ['Fiches de révision', 'Revision sheets'],
  f7: ['Tuteur IA élève', 'Student AI tutor'],
};

export default function AIAdminSchool() {
  const { language } = useLanguage();
  const fr = language === 'fr';
  const [data, setData] = useState(null);
  const load = () => aiAPI.getSchool().then((r) => setData(r.data)).catch(() => {});
  useEffect(() => { load(); }, []);

  if (!data) return <div className="flex justify-center p-12"><div className="animate-spin w-8 h-8 border-2 border-emerald-600 border-t-transparent rounded-full" /></div>;
  const s = data.settings;

  const save = async (patch) => {
    try { await aiAPI.updateSchool(patch); load(); toast.success(fr ? 'Enregistré' : 'Saved'); }
    catch { toast.error(fr ? 'Erreur lors de la mise à jour' : 'Update failed'); }
  };

  if (!data.global_enabled || !s.enabled) {
    return (
      <div className="space-y-4" data-testid="ai-admin-school-page">
        <h1 className="text-2xl font-outfit font-bold text-slate-900 flex items-center gap-2"><Sparkles className="w-6 h-6 text-emerald-600" />IA</h1>
        <Card className="border-amber-200 bg-amber-50"><CardContent className="p-5 text-amber-800 text-sm">
          {fr ? "L'IA n'est pas activée pour votre école. Contactez Smart Daara pour l'activer." : 'AI is not enabled for your school. Contact Smart Daara to enable it.'}
        </CardContent></Card>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="ai-admin-school-page">
      <h1 className="text-2xl font-outfit font-bold text-slate-900 flex items-center gap-2">
        <Sparkles className="w-6 h-6 text-emerald-600" />{fr ? 'IA — Mon école' : 'AI — My school'}
      </h1>
      <Card className="border-0 shadow-sm">
        <CardHeader><CardTitle className="text-base">{fr ? 'Fonctions' : 'Features'}</CardTitle></CardHeader>
        <CardContent className="space-y-2">
          {Object.entries(FN_LABELS).map(([f, labels]) => (
            <div key={f} className="flex items-center justify-between px-3 py-2 bg-slate-50 rounded-lg">
              <span>{labels[fr ? 0 : 1]} <span className="text-xs text-slate-400 uppercase">({f})</span></span>
              <Switch checked={!!s.functions?.[f]} data-testid={`ai-fn-switch-${f}`} onCheckedChange={(v) =>
                save({ functions: { ...s.functions, [f]: v } })} />
            </div>
          ))}
        </CardContent>
      </Card>
      <Card className="border-0 shadow-sm">
        <CardHeader><CardTitle className="text-base">{fr ? 'Réglages' : 'Settings'}</CardTitle></CardHeader>
        <CardContent className="grid sm:grid-cols-3 gap-4 items-end">
          <div>
            <Label className="text-xs">{fr ? 'Questions tuteur / élève / jour' : 'Tutor questions / student / day'}</Label>
            <Input type="number" defaultValue={s.tutor_daily_limit} onBlur={(e) => save({ tutor_daily_limit: parseInt(e.target.value) || 20 })} data-testid="tutor-limit-input" />
          </div>
          <div>
            <Label className="text-xs">{fr ? 'Publication auto des fiches' : 'Auto-publish sheets'}</Label>
            <div className="pt-2"><Switch checked={!!s.auto_publish_sheets} data-testid="auto-publish-switch" onCheckedChange={(v) => save({ auto_publish_sheets: v })} /></div>
          </div>
          <div>
            <Label className="text-xs">{fr ? 'Langue par défaut' : 'Default language'}</Label>
            <select className="w-full h-10 border border-slate-200 rounded-md px-2 text-sm" value={s.default_language} data-testid="default-language-select"
              onChange={(e) => save({ default_language: e.target.value })}>
              <option value="fr">Français</option><option value="en">English</option>
            </select>
          </div>
        </CardContent>
      </Card>
      <p className="text-sm text-slate-500">{fr ? 'Consommation IA de l\'école' : 'School AI usage'} : <b>{data.usage_count}</b> {fr ? 'requêtes' : 'requests'} · ~${data.cost_usd}</p>
      <p className="text-xs text-slate-400">{fr ? "Certaines fonctionnalités utilisent un service d'IA. Les propositions de l'IA sont vérifiées par l'enseignant." : 'Some features use an AI service. AI suggestions are reviewed by the teacher.'}</p>
    </div>
  );
}
